import React, { useEffect, useState, memo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'

import {
    Pool,
    useCompanyGetPoolsWithNfcInfoQuery,
    useCompanySetPoolsNfcInfoMutation,
} from '../../../common/redux/api/web-api'
import { Table, TableBody, TableHeader, TableBodyCell, TableRow } from '../../../lib/table'
import { notifySuccess, notifyError } from '../../../common/features/notificationsManager/Notify'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { WidgetLayout } from '../../../lib/widget-layout'
import { Toggle } from '../../../common/components/Toggle'
import { Button } from '../../../common/components/Button'
import { RootState } from '../../redux/store'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'

const ButtonContainer = styled.div`
    margin-bottom: 20px;
    justify-content: flex-end;
    display: flex;
    gap: 4px;
`

export const UserPoolsPage = memo(function AdminUsersWidget(): React.ReactElement {
    const text = useText()
    const [pools, setPools] = useState<Pool[]>([])

    const { id: userId } = useParams()
    const navigate = useNavigate()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const { data, isLoading: loadingInitialData } = useCompanyGetPoolsWithNfcInfoQuery(
        { companyId, userId },
        { skip: !userId, refetchOnMountOrArgChange: true }
    )
    const [updateAccess, { isLoading }] = useCompanySetPoolsNfcInfoMutation()

    useEffect(() => {
        if (data) setPools([...data])
    }, [data])

    const updateNfcAccess = (pool: Pool) => {
        const newPoolsArray = pools.map((p) => {
            if (pool.id === p.id) return { ...pool, hasNfcAccess: !pool.hasNfcAccess }
            return p
        })

        setPools(newPoolsArray)
    }

    const save = async () => {
        try {
            await updateAccess({ userId: userId, companyId: companyId, body: pools }).unwrap()
            notifySuccess(text('lblRFIDAccessToPoolsUpdated'), true)
            navigate(`/user/${userId}`)
        } catch (e) {
            console.error(e)
            notifyError(text('lblSomethingWentWrong'), true)
        }
    }

    return (
        <PageLayoutContainer>
            <ButtonContainer>
                <Button
                    size="sm"
                    label={text('btnSave')}
                    onClick={() => save()}
                    loading={isLoading}
                    data-testid="btnSave"
                ></Button>
                <Button
                    size="sm"
                    label={text('btnCancel')}
                    onClick={() => navigate(`/user/${userId}`)}
                    data-testid="btnCancel"
                ></Button>
            </ButtonContainer>
            <WidgetLayout title={text('lblPoolsPage')}>
                {userId && (
                    <Table data-testid="table-rfid-access">
                        <TableHeader>
                            <TableRow>
                                <TableBodyCell data-testid="h-cell-name">
                                    <Text code="lblName" />
                                </TableBodyCell>
                                <TableBodyCell data-testid="h-cell-access">
                                    <Text code="lblRFIDAccess" />
                                </TableBodyCell>
                            </TableRow>
                        </TableHeader>
                        {!loadingInitialData && (
                            <TableBody>
                                {pools?.map((p) => (
                                    <TableRow key={p.id}>
                                        <TableBodyCell data-testid="b-cell-name">{p.name}</TableBodyCell>
                                        <TableBodyCell data-testid="b-cell-access">
                                            <Toggle
                                                data-testid="pools-toggle"
                                                isToggled={p.hasNfcAccess ?? false}
                                                toggleFn={() => updateNfcAccess(p)}
                                            ></Toggle>
                                        </TableBodyCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        )}
                    </Table>
                )}
                {loadingInitialData && <div className="shimmer-container shimmer-small"></div>}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
